/**
 * Accounting API Service
 * 
 * Integrates with backend accounting compliance system
 * Handles: GL operations, fiscal periods, reconciliation, hierarchy
 */

import api from '../utils/api';

// ==================== FISCAL PERIOD MANAGEMENT ====================

export const fiscalPeriodAPI = {
  // Create new fiscal period
  createPeriod: (data) => api.post('/api/fiscal-periods/', data),
  
  // Get specific period
  getPeriod: (periodId) => api.get(`/api/fiscal-periods/${periodId}`),
  
  // Get all periods
  getAllPeriods: (params) => api.get('/api/fiscal-periods/', { params }),
  
  // Get current active period
  getCurrentPeriod: () => api.get('/api/fiscal-periods/current'),
  
  // Get period by date
  getPeriodByDate: (date) => api.get(`/api/fiscal-periods/by-date/${date}`),
  
  // Lock period (prevent posting)
  lockPeriod: (periodId) => api.post(`/api/fiscal-periods/${periodId}/lock`),
  
  // Close period (create closing entries)
  closePeriod: (periodId) => api.post(`/api/fiscal-periods/${periodId}/close`),
  
  // Get summary across all periods (school-wide — there's no per-period summary endpoint)
  getPeriodSummary: () => api.get('/api/fiscal-periods/summary'),
  
  // Check if can post
  canPostToDate: (postedDate, isAdjusting = false) => 
    api.get(`/api/date-separation/can-post/${postedDate}`, {
      params: { is_adjusting_entry: isAdjusting }
    }),
};

// ==================== GL AUDIT TRAIL ====================

export const auditTrailAPI = {
  // Get audit trail for entity
  getEntityAuditTrail: (entityType, entityId) => 
    api.get(`/api/gl-audit-logs/entity/${entityType}/${entityId}`),
  
  // Get audit trail by action
  getAuditByAction: (action) => 
    api.get(`/api/gl-audit-logs/action/${action}`),
  
  // Get audit trail by user
  getAuditByUser: (userId) => 
    api.get(`/api/gl-audit-logs/user/${userId}`),
  
  // Get audit trail for date range
  getAuditByDateRange: (startDate, endDate) => 
    api.get('/api/gl-audit-logs/date-range', {
      params: { start_date: startDate, end_date: endDate }
    }),
  
  // Get audit summary for reporting
  getAuditSummary: () =>
    api.get('/api/gl-audit-logs/report/summary'),

  // Get recent activity (for dashboards)
  getRecentActivity: (hours = 24, limit = 5) =>
    api.get('/api/gl-audit-logs/report/recent', { params: { hours, limit } }),

  // Export audit trail
  exportAuditTrail: (entityType, entityId) => 
    api.get(`/api/gl-audit-logs/export/${entityType}/${entityId}`),
};

// ==================== RETAINED EARNINGS ====================

export const retainedEarningsAPI = {
  // Calculate net income for period
  calculateNetIncome: (periodId) => 
    api.get(`/api/retained-earnings/net-income/${periodId}`),
  
  // Close period (create closing entries)
  closePeriod: (periodId) => 
    api.post(`/api/retained-earnings/close-period/${periodId}`),
  
  // Set opening balances
  setOpeningBalances: (fromPeriodId, toPeriodId) => 
    api.post('/api/retained-earnings/set-opening-balances', {
      from_period_id: fromPeriodId,
      to_period_id: toPeriodId
    }),
  
  // Get post-closing trial balance
  getPostClosingTrialBalance: (periodId) => 
    api.get(`/api/retained-earnings/post-closing-trial-balance/${periodId}`),
  
  // Verify period is properly closed
  verifyPeriodClosed: (periodId) => 
    api.get(`/api/retained-earnings/verify-period-close/${periodId}`),
  
  // Get retained earnings balance
  getREBalance: () => 
    api.get('/api/retained-earnings/balance'),
};

// ==================== DATE SEPARATION ====================

export const dateSeparationAPI = {
  // Check posting eligibility
  checkPostingEligibility: (postedDate, isAdjustingEntry = false) => 
    api.get(`/api/date-separation/can-post/${postedDate}`, {
      params: { is_adjusting_entry: isAdjustingEntry }
    }),
  
  // Get cutoff entries
  getCutoffEntries: (periodId) => 
    api.get(`/api/date-separation/cutoff-entries/${periodId}`),
  
  // Get accrual entries
  getAccrualEntries: (periodId) => 
    api.get(`/api/date-separation/accrual-entries/${periodId}`),
  
  // Get adjusting entries
  getAdjustingEntries: (periodId) => 
    api.get(`/api/date-separation/adjusting-entries/${periodId}`),
  
  // Query by entry date
  getByEntryDate: (startDate, endDate, status = null) => 
    api.get('/api/date-separation/by-entry-date', {
      params: { start_date: startDate, end_date: endDate, posting_status: status }
    }),
  
  // Query by posting date
  getByPostingDate: (startDate, endDate) => 
    api.get('/api/date-separation/by-posting-date', {
      params: { start_date: startDate, end_date: endDate }
    }),
  
  // Get date variance report
  getDateVarianceReport: (periodId) => 
    api.get(`/api/date-separation/date-variance-report/${periodId}`),
};

// ==================== REVERSAL ENTRIES ====================

export const reversalAPI = {
  // Reverse full entry
  reverseFullEntry: (entryId, data) =>
    api.post(`/api/reversals/${entryId}/reverse-full`, data),
  
  // Reverse partial entry
  reversePartialEntry: (entryId, data) => 
    api.post(`/api/reversals/${entryId}/reverse-partial`, data),
  
  // Reverse specific accounts
  reverseSpecificAccounts: (entryId, data) => 
    api.post(`/api/reversals/${entryId}/reverse-accounts`, data),
  
  // Get reversal chain
  getReversalChain: (entryId) => 
    api.get(`/api/reversals/${entryId}/chain`),
  
  // Get reversals for period
  getPeriodReversals: (periodId) => 
    api.get('/api/reversals/period-reversals', {
      params: { period_id: periodId }
    }),
  
  // Get reversal statistics
  getReversalStats: () => 
    api.get('/api/reversals/statistics'),
};

// ==================== BANK RECONCILIATION ====================

export const bankReconciliationAPI = {
  // Import bank statement
  importBankStatement: (accountId, data) => 
    api.post('/api/bank-reconciliation/import', {
      account_id: accountId,
      ...data
    }),
  
  // Auto-match transactions
  autoMatchTransactions: (reconciliationId, matchingWindowDays = 5) => 
    api.post(`/api/bank-reconciliation/auto-match/${reconciliationId}`, {
      matching_window_days: matchingWindowDays
    }),
  
  // Manually match transaction
  manuallyMatch: (bankStatementId, journalEntryId, varianceReason = null) => 
    api.post(`/api/bank-reconciliation/match/${bankStatementId}/${journalEntryId}`, {
      variance_reason: varianceReason
    }),
  
  // Calculate variance
  calculateVariance: (reconciliationId) => 
    api.get(`/api/bank-reconciliation/variance/${reconciliationId}`),
  
  // Get reconciling items
  getReconcilingItems: (reconciliationId) => 
    api.get(`/api/bank-reconciliation/reconciling-items/${reconciliationId}`),
  
  // Get reconciliation summary
  getSummary: (reconciliationId) => 
    api.get(`/api/bank-reconciliation/summary/${reconciliationId}`),
  
  // Complete reconciliation
  completeReconciliation: (reconciliationId) => 
    api.post(`/api/bank-reconciliation/complete/${reconciliationId}`),
};

// ==================== SUB-LEDGER RECONCILIATION ====================

export const subLedgerReconciliationAPI = {
  // Create reconciliation
  createReconciliation: (data) => 
    api.post('/api/subledger-reconciliation/create', data),
  
  // Auto-match details
  autoMatchDetails: (reconciliationId) => 
    api.post(`/api/subledger-reconciliation/auto-match/${reconciliationId}`),
  
  // Get aging analysis
  getAgingAnalysis: (reconciliationId) => 
    api.get(`/api/subledger-reconciliation/aging/${reconciliationId}`),
  
  // Calculate variance
  calculateVariance: (reconciliationId) => 
    api.get(`/api/subledger-reconciliation/variance/${reconciliationId}`),
  
  // Get unmatched details
  getUnmatchedDetails: (reconciliationId) => 
    api.get(`/api/subledger-reconciliation/unmatched/${reconciliationId}`),
  
  // Get summary
  getSummary: (reconciliationId) => 
    api.get(`/api/subledger-reconciliation/summary/${reconciliationId}`),
  
  // Complete reconciliation
  completeReconciliation: (reconciliationId) => 
    api.post(`/api/subledger-reconciliation/complete/${reconciliationId}`),
};

// ==================== ACCOUNT HIERARCHY ====================

export const accountHierarchyAPI = {
  // Create hierarchy
  createHierarchy: (data) => 
    api.post('/api/account-hierarchy/hierarchies', data),
  
  // Create hierarchy node
  createNode: (data) => 
    api.post('/api/account-hierarchy/nodes', data),
  
  // Add relationship
  addRelationship: (data) => 
    api.post('/api/account-hierarchy/relationships', data),
  
  // Rollup all nodes
  rollupAllNodes: (hierarchyId) => 
    api.post(`/api/account-hierarchy/rollup/${hierarchyId}`),
  
  // Get node balance
  getNodeBalance: (nodeId) => 
    api.get(`/api/account-hierarchy/node-balance/${nodeId}`),
  
  // Get hierarchy tree
  getHierarchyTree: (hierarchyId, rootNodeId = null) => 
    api.get(`/api/account-hierarchy/tree/${hierarchyId}`, {
      params: { root_node_id: rootNodeId }
    }),
  
  // Get consolidated report
  getConsolidatedReport: (hierarchyId) => 
    api.get(`/api/account-hierarchy/consolidated-report/${hierarchyId}`),
  
  // Export hierarchy
  exportHierarchy: (hierarchyId, format = 'json') => 
    api.get(`/api/account-hierarchy/export/${hierarchyId}`, {
      params: { format }
    }),
};

// ==================== JOURNAL ENTRIES (Enhanced) ====================

export const journalEntryAPI = {
  // Create entry
  createEntry: (data) =>
    api.post('/api/journal-entries', data),
  
  // Get entry
  getEntry: (entryId) => 
    api.get(`/api/journal-entries/${entryId}`),
  
  // Get entries by reference
  getEntriesByReference: (referenceType, referenceId) => 
    api.get(`/api/journal-entries/reference/${referenceType}/${referenceId}`),
  
  // Post entry to GL
  postEntry: (entryId, data) => 
    api.post(`/api/journal-entries/${entryId}/post`, data),
  
  // Get trial balance
  getTrialBalance: () => 
    api.get('/api/journal-entries/trial-balance'),
};

// ==================== CHART OF ACCOUNTS (Enhanced) ====================

export const chartOfAccountsAPI = {
  // Create account
  createAccount: (data) =>
    api.post('/api/chart-of-accounts', data),

  // Get account
  getAccount: (accountId) =>
    api.get(`/api/chart-of-accounts/${accountId}`),

  // Get accounts by type
  getAccountsByType: (accountType) =>
    api.get('/api/chart-of-accounts', {
      params: { account_type: accountType }
    }),

  // Get all accounts
  getAllAccounts: () =>
    api.get('/api/chart-of-accounts'),

  // Update account
  updateAccount: (accountId, data) =>
    api.put(`/api/chart-of-accounts/${accountId}`, data),
  
  // Get account balance
  getAccountBalance: (accountId, useCached = true) =>
    api.get(`/api/chart-of-accounts/${accountId}/balance`, {
      params: { use_cached: useCached }
    }),

  // Recalculate all balances
  recalculateAllBalances: () =>
    api.post('/api/chart-of-accounts/recalculate-balances'),
};

// ==================== EXPENSES (Enhanced) ====================

export const expensesAPI = {
  // Create expense
  createExpense: (data) =>
    api.post('/api/expenses', data),

  // Get expense
  getExpense: (expenseId) =>
    api.get(`/api/expenses/${expenseId}`),

  // Get all expenses
  getAllExpenses: (params) =>
    api.get('/api/expenses', { params }),

  // Approve expense
  approveExpense: (expenseId) =>
    api.post(`/api/expenses/${expenseId}/approve`),

  // Reject expense
  rejectExpense: (expenseId, rejectionReason) =>
    api.post(`/api/expenses/${expenseId}/reject`, { rejection_reason: rejectionReason }),

  // Post expense to GL
  postToGL: (expenseId) =>
    api.post(`/api/expenses/${expenseId}/post`),
};

export default {
  fiscalPeriodAPI,
  auditTrailAPI,
  retainedEarningsAPI,
  dateSeparationAPI,
  reversalAPI,
  bankReconciliationAPI,
  subLedgerReconciliationAPI,
  accountHierarchyAPI,
  journalEntryAPI,
  chartOfAccountsAPI,
  expensesAPI,
};
